import { useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { MessageCircle, Loader2, X, ExternalLink, Copy } from 'lucide-react';

export default function WhatsAppPO({ manufacturerId, manufacturerName }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const generate = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/manufacturers/${manufacturerId}/whatsapp`);
            setData(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not generate WhatsApp PO');
        } finally {
            setLoading(false);
        }
    };

    const copyBody = async () => {
        if (!data) return;
        try {
            await navigator.clipboard.writeText(data.body);
            toast.success('Message copied');
        } catch {
            toast.error('Copy failed');
        }
    };

    if (!data) {
        return (
            <button
                data-testid={`wa-generate-${manufacturerId}`}
                onClick={generate}
                disabled={loading}
                className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-700 hover:border-[#25D366] hover:text-[#25D366] font-display uppercase text-xs tracking-wider disabled:opacity-50"
            >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <MessageCircle size={14} />}
                {loading ? 'Building…' : 'WhatsApp PO'}
            </button>
        );
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
             data-testid={`wa-modal-${manufacturerId}`}>
            <div className="bg-ink border border-zinc-800 max-w-2xl w-full max-h-[90vh] flex flex-col">
                <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-zinc-800 bg-surface">
                    <div className="flex items-center gap-2">
                        <MessageCircle size={16} className="text-[#25D366]" />
                        <div>
                            <div className="font-display uppercase text-sm">WhatsApp PO · {manufacturerName}</div>
                            <div className="font-mono-data text-[10px] text-zinc-500">
                                {data.po_ref} · {data.item_count} SKUs / {data.total_units} units · {data.char_count} chars
                            </div>
                        </div>
                    </div>
                    <button onClick={() => setData(null)} className="p-1 text-zinc-400 hover:text-[#25D366]">
                        <X size={16} />
                    </button>
                </div>

                <div className="px-5 py-3 border-b border-zinc-800/60 font-mono-data text-xs">
                    <span className="text-zinc-500">To:</span>{' '}
                    <span className="text-zinc-200">{data.phone}</span>
                </div>

                <pre className="flex-1 px-5 py-4 font-mono-data text-xs text-zinc-300 whitespace-pre-wrap overflow-y-auto leading-relaxed">
{data.body}
                </pre>

                <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-zinc-800 bg-surface">
                    <button
                        data-testid={`wa-copy-${manufacturerId}`}
                        onClick={copyBody}
                        className="inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-700 hover:border-[#25D366] hover:text-[#25D366] font-display uppercase text-[10px] tracking-wider"
                    >
                        <Copy size={12} /> Copy
                    </button>
                    <a
                        data-testid={`wa-open-${manufacturerId}`}
                        href={data.wa_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#25D366] hover:bg-[#1ebe57] text-black font-display uppercase text-[10px] tracking-wider"
                    >
                        <ExternalLink size={12} /> Open WhatsApp
                    </a>
                </div>
            </div>
        </div>
    );
}
